import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, categories } from '@/mocks/videoData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredVideos = useMemo(() => {
    return videos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const currentCat = categories.find((c) => c.slug === activeCategory);

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCat?.name || ''} - 精品视频`,
    "description": `精品视频${currentCat?.name || '视频'}分类，精选高清优质${currentCat?.name || ''}内容，画质清晰、更新及时。`,
    "url": `https://www.jingpinshipin.com/category/${activeCategory}`,
  };

  return (
    <>
      <SEOHead
        title={`${currentCat?.name || '分类'}视频`}
        description={`精品视频${currentCat?.name || '视频'}分类，精选高清优质${currentCat?.name || ''}内容，画质清晰、更新及时、分类精准。`}
        keywords={`${currentCat?.name || '视频'},精品视频,高清视频,在线观看`}
        canonicalUrl={`https://www.jingpinshipin.com/category/${activeCategory}`}
        schema={schema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('home_breadcrumb'), href: '/' },
                { label: t('category_breadcrumb') },
                { label: currentCat?.name || '' },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-6 mb-2">
              {currentCat?.name || ''}
              <span className="text-base font-normal text-foreground-400 ml-3">{filteredVideos.length} 部作品</span>
            </h1>
            <p className="text-foreground-500 text-sm mb-8">
              精选{currentCat?.name || ''}分类下的优质视频内容，为您提供最佳的观看体验
            </p>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-video-line text-2xl"></i>
                </span>
                <p className="text-foreground-500">{t('no_videos')}</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}